import {
    ApolloClient,
    ApolloLink,
    ApolloProvider,
    createHttpLink,
    InMemoryCache,
    ServerError,
} from "@apollo/client";
import {v4 as uid} from "uuid";

import {onError} from "@apollo/client/link/error";
import {setContext} from "@apollo/client/link/context";
import {Body} from "@apollo/client/link/http/selectHttpOptionsAndBody";
import React, {ReactNode, useMemo} from "react";
import {NetworkError} from "@apollo/client/errors";
import {getenv} from "../utils/getenv";

export const IS_EMBEDDED = window !== window.parent;
export const AUTH_TRANSFER = "AUTH_TRANSFER";

type ApiProviderProps = {children: ReactNode; onLogout: () => void};

const customFetch = (uri: string, options: RequestInit) => {
    if (options.body instanceof FormData) return fetch(uri, options);
    const body: Body = JSON.parse(options.body as string) as Body;
    const {operationName} = body;

    const paramsArray = [`opname=${encodeURIComponent(operationName as string)}`];

    const filteredVariablesString = paramsArray.join("&");
    return fetch(`${uri}?${filteredVariablesString}`, options);
};

function isServerError(networkError: NetworkError | undefined): networkError is ServerError {
    if (!networkError) return false;
    return "statusCode" in networkError;
}

export function ApiProvider(props: Readonly<ApiProviderProps>) {
    const GATEWAY_URL = getenv("GATEWAY_URL");
    const VERSION = getenv("VERSION") ?? "unknown";
    const client = useMemo(() => {
        const httpLink = createHttpLink({
            uri: `${GATEWAY_URL}/graphql`,
            fetch: customFetch,
            headers: {
                "keep-alive": "true",
            },
        });

        const authLink = setContext((_, {headers}) => {
            // generate unique request id and setting it as Sentry tag and attaching it to every request
            const requestId = uid();

            const modifiedHeaders = {
                ...(headers as Record<string, any>),
                authorization: null,
                "X-Request-Id": requestId,
            };

            // return the headers to the context so httpLink can read them
            return {
                headers: modifiedHeaders,
            };
        });

        const errorLink = onError(({networkError}) => {
            if (!isServerError(networkError)) return;

            if (networkError.statusCode === 401) {
                props.onLogout();
            }
        });

        const link = ApolloLink.from([errorLink, authLink, httpLink]);

        const client = new ApolloClient({
            link,
            cache: new InMemoryCache(),
            connectToDevTools: true,
            name: "prospect-guidance-ui",
            version: VERSION,
        });

        return client;
    }, [GATEWAY_URL, VERSION, props]);

    return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
}
